import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';

interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Styles the confirm button for a destructive action (delete, cancel booking, ...). */
  danger?: boolean;
}

interface ConfirmRequest extends ConfirmOptions {
  message: string;
  resolve: (ok: boolean) => void;
}

let setRequest: ((req: ConfirmRequest | null) => void) | null = null;

/**
 * Promise-based replacement for `window.confirm()`, styled to match the rest of the
 * app instead of popping the browser's native dialog. `<ConfirmDialogHost />` is
 * mounted once near the app root (MainLayout); this function just hands it a request.
 * Resolves `true`/`false`, never rejects.
 */
export function confirm(message: string, options: ConfirmOptions = {}): Promise<boolean> {
  return new Promise((resolve) => {
    if (!setRequest) {
      // Host not mounted for some reason - fail safe rather than silently do nothing.
      resolve(window.confirm(message));
      return;
    }
    setRequest({ message, resolve, ...options });
  });
}

/** Mount once near the app root. Renders the current confirm() request, if any. */
export const ConfirmDialogHost: React.FC = () => {
  const [request, setRequestState] = useState<ConfirmRequest | null>(null);

  useEffect(() => {
    setRequest = setRequestState;
    return () => {
      setRequest = null;
    };
  }, []);

  const close = useCallback(
    (ok: boolean) => {
      request?.resolve(ok);
      setRequestState(null);
    },
    [request]
  );

  useEffect(() => {
    if (!request) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request, close]);

  if (!request) return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] grid place-items-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in"
        onClick={() => close(false)}
        aria-hidden
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={request.title ?? 'Confirm'}
        className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#13141c] p-6 shadow-[0_32px_80px_-24px_rgba(0,0,0,0.9)] animate-scale-in"
      >
        {request.title && <h2 className="text-h3 text-white mb-2">{request.title}</h2>}
        <p className="text-sm leading-relaxed text-white/70">{request.message}</p>
        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" variant="ghost" className="border border-white/15" onClick={() => close(false)}>
            {request.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            type="button"
            variant={request.danger ? 'primary' : 'secondary'}
            onClick={() => close(true)}
          >
            {request.confirmLabel ?? (request.danger ? 'Delete' : 'Confirm')}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};
